/**
 * Pure retention-hook rules (daily bonus, wheel, gem ads).
 */

import {
  RETENTION_DAILY_REWARDS,
  RETENTION_DAILY_TRACK_LENGTH,
  RETENTION_GEMS_AD_COOLDOWN_MS,
  RETENTION_WHEEL_FREE_SPIN_COOLDOWN_MS,
  RETENTION_WHEEL_SEGMENTS,
} from "../config/retention-config.js";

/** Wheel winner icon+amount claim pop (grow 0.5s, then grow+fade 0.5s). */
export var WHEEL_CLAIM_REWARD_ANIM_MS = 1000;
/** Wheel spin CSS transition duration — tuned to `wheel-spin.mp3` (~3.08 s). */
export var WHEEL_SPIN_ANIMATION_MS = 2548;
/** Wheel spin easing — fast start, long deceleration tail aligned with spin SFX. */
export var WHEEL_SPIN_EASING = "cubic-bezier(0.1, 0.84, 0.16, 1)";
/** Safety timeout for wheel spin `transitionend` (animation ms + slack). */
export var WHEEL_SPIN_FALLBACK_MS = WHEEL_SPIN_ANIMATION_MS + 120;

/**
 * @param {Date} [date]
 * @returns {string}
 */
export function getLocalDateString(date) {
  var d = date instanceof Date ? date : new Date();
  var y = d.getFullYear();
  var m = String(d.getMonth() + 1).padStart(2, "0");
  var day = String(d.getDate()).padStart(2, "0");
  return y + "-" + m + "-" + day;
}

/**
 * @returns {import("../config/retention-config.js").RetentionGrant}
 */
export function getDefaultRetentionState() {
  return {
    dailyBonusStep: 1,
    dailyBonusLastClaimDate: "",
    dailyBonusAutoShownDate: "",
    wheelLastFreeSpinAt: 0,
    wheelLastAdSpinAt: 0,
    gemsAdLastAt: 0,
  };
}

/**
 * @param {Record<string, unknown> | null | undefined} raw
 * @returns {ReturnType<typeof getDefaultRetentionState>}
 */
export function normalizeRetentionState(raw) {
  var defaults = getDefaultRetentionState();
  if (!raw || typeof raw !== "object") return defaults;
  var step = Math.floor(Number(raw.dailyBonusStep) || 1);
  return {
    dailyBonusStep: Math.max(1, Math.min(RETENTION_DAILY_TRACK_LENGTH, step)),
    dailyBonusLastClaimDate: typeof raw.dailyBonusLastClaimDate === "string"
      ? raw.dailyBonusLastClaimDate
      : defaults.dailyBonusLastClaimDate,
    dailyBonusAutoShownDate: typeof raw.dailyBonusAutoShownDate === "string"
      ? raw.dailyBonusAutoShownDate
      : defaults.dailyBonusAutoShownDate,
    wheelLastFreeSpinAt: Math.max(0, Math.floor(Number(raw.wheelLastFreeSpinAt) || 0)),
    wheelLastAdSpinAt: Math.max(0, Math.floor(Number(raw.wheelLastAdSpinAt) || 0)),
    gemsAdLastAt: Math.max(0, Math.floor(Number(raw.gemsAdLastAt) || 0)),
  };
}

/**
 * @param {ReturnType<typeof getDefaultRetentionState>} state
 * @param {number} [nowMs]
 * @returns {string}
 */
export function getLocalDateStringFromMs(nowMs) {
  return getLocalDateString(new Date(nowMs != null ? nowMs : Date.now()));
}

/**
 * @param {ReturnType<typeof getDefaultRetentionState>} state
 * @param {number} [nowMs]
 * @returns {boolean}
 */
export function isDailyClaimAvailable(state, nowMs) {
  var today = getLocalDateStringFromMs(nowMs);
  return state.dailyBonusLastClaimDate !== today;
}

/**
 * @typedef {{ currentRewardDay: number, isTodayClaimed: boolean }} DailyRewardTrackView
 */

/**
 * Single source of truth for the daily reward track UI.
 * `currentRewardDay` is which day slot is "today" (1–7).
 * `isTodayClaimed` is whether that slot was already claimed this calendar day.
 * @param {ReturnType<typeof getDefaultRetentionState>} state
 * @param {number} [nowMs]
 * @returns {DailyRewardTrackView}
 */
export function getDailyRewardTrackView(state, nowMs) {
  var normalized = normalizeRetentionState(state);
  var today = getLocalDateStringFromMs(nowMs);
  var isTodayClaimed = normalized.dailyBonusLastClaimDate === today;
  var trackStep = normalized.dailyBonusStep;
  var currentRewardDay;
  if (!isTodayClaimed) {
    currentRewardDay = trackStep;
  } else if (trackStep === 1) {
    currentRewardDay = RETENTION_DAILY_TRACK_LENGTH;
  } else {
    currentRewardDay = trackStep - 1;
  }
  return {
    currentRewardDay: currentRewardDay,
    isTodayClaimed: isTodayClaimed,
  };
}

/**
 * @param {DailyRewardTrackView} view
 * @returns {number} 0 when there is no tomorrow tile, otherwise 1–7
 */
export function getDailyTomorrowRewardDay(view) {
  var current = Math.floor(Number(view.currentRewardDay) || 1);
  if (current >= RETENTION_DAILY_TRACK_LENGTH) return 0;
  return current + 1;
}

/**
 * @param {number} tileDay
 * @param {DailyRewardTrackView} view
 * @returns {{ label: string, status: "claimed" | "current" | "locked" }}
 */
export function getDailyTilePresentation(tileDay, view) {
  var day = Math.floor(Number(tileDay) || 0);
  var tomorrow = getDailyTomorrowRewardDay(view);
  var current = Math.floor(Number(view.currentRewardDay) || 1);
  var label;
  if (day === current) {
    label = "Today";
  } else if (tomorrow > 0 && day === tomorrow) {
    label = "Tomorrow";
  } else {
    label = day === RETENTION_DAILY_TRACK_LENGTH ? "Day 7" : "Day " + day;
  }
  var status;
  if (!view.isTodayClaimed) {
    if (day < current) status = "claimed";
    else if (day === current) status = "current";
    else status = "locked";
  } else if (current >= RETENTION_DAILY_TRACK_LENGTH) {
    status = "claimed";
  } else if (day <= current) {
    status = "claimed";
  } else {
    status = "locked";
  }
  return { label: label, status: status };
}

/**
 * Milliseconds until the next local midnight.
 * @param {number} [nowMs]
 * @returns {number}
 */
export function getMsUntilLocalMidnight(nowMs) {
  var d = new Date(nowMs != null ? nowMs : Date.now());
  var nextMidnight = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1, 0, 0, 0, 0);
  return Math.max(0, nextMidnight.getTime() - d.getTime());
}

/**
 * Daily claim cooldown: time until next local day when already claimed today.
 * @param {ReturnType<typeof getDefaultRetentionState>} state
 * @param {number} [nowMs]
 * @returns {number}
 */
export function getDailyClaimCooldownRemainingMs(state, nowMs) {
  var today = getLocalDateStringFromMs(nowMs);
  if (normalizeRetentionState(state).dailyBonusLastClaimDate !== today) return 0;
  return getMsUntilLocalMidnight(nowMs);
}

/**
 * @param {ReturnType<typeof getDefaultRetentionState>} state
 * @param {number} [nowMs]
 * @param {boolean} hasCompletedARun
 * @returns {boolean}
 */
export function shouldAutoOpenDailyBonus(state, nowMs, hasCompletedARun) {
  if (!hasCompletedARun) return false;
  if (!isDailyClaimAvailable(state, nowMs)) return false;
  var today = getLocalDateStringFromMs(nowMs);
  return state.dailyBonusAutoShownDate !== today;
}

/**
 * Session-return auto-open: same gates as shouldAutoOpenDailyBonus, but only from
 * the player's second local calendar day onward (dayIndex >= 1).
 * @param {ReturnType<typeof getDefaultRetentionState>} state
 * @param {number} [nowMs]
 * @param {boolean} hasCompletedARun
 * @param {number} dayIndex
 * @returns {boolean}
 */
export function shouldAutoOpenDailyBonusOnReturn(state, nowMs, hasCompletedARun, dayIndex) {
  if (Math.floor(Number(dayIndex) || 0) < 1) return false;
  return shouldAutoOpenDailyBonus(state, nowMs, hasCompletedARun);
}

/**
 * @param {ReturnType<typeof getDefaultRetentionState>} state
 * @param {number} [nowMs]
 * @returns {boolean}
 */
export function isFreeWheelSpinReady(state, nowMs) {
  var now = nowMs != null ? nowMs : Date.now();
  var last = Math.max(0, Number(state.wheelLastFreeSpinAt) || 0);
  if (!last) return true;
  return now - last >= RETENTION_WHEEL_FREE_SPIN_COOLDOWN_MS;
}

/**
 * @param {number} lastAt
 * @param {number} cooldownMs
 * @param {number} [nowMs]
 * @returns {number}
 */
export function getCooldownRemainingMs(lastAt, cooldownMs, nowMs) {
  var now = nowMs != null ? nowMs : Date.now();
  var last = Math.max(0, Number(lastAt) || 0);
  if (!last) return 0;
  return Math.max(0, cooldownMs - (now - last));
}

/**
 * @param {number} ms
 * @returns {string}
 */
export function formatCooldownLabel(ms) {
  var totalSec = Math.ceil(Math.max(0, ms) / 1000);
  if (totalSec >= 3600) {
    var h = Math.floor(totalSec / 3600);
    var m = Math.floor((totalSec % 3600) / 60);
    var s = totalSec % 60;
    return h + ":" + String(m).padStart(2, "0") + ":" + String(s).padStart(2, "0");
  }
  var min = Math.floor(totalSec / 60);
  var sec = totalSec % 60;
  return String(min).padStart(2, "0") + ":" + String(sec).padStart(2, "0");
}

/**
 * Inked cartoon hourglass (same markup as the static badges in index.html): a pale glass
 * with gold sand and gold caps, each ringed in ink.
 * @type {string}
 */
export var RETENTION_COOLDOWN_HOURGLASS_HTML =
  '<svg class="retention-cooldown-badge__hourglass" viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
  '<path d="M7.5 5.5h9c0 3-2.6 4.6-4.5 6.5 1.9 1.9 4.5 3.5 4.5 6.5h-9c0-3 2.6-4.6 4.5-6.5C10.1 10.1 7.5 8.5 7.5 5.5z"' +
  ' fill="#e9f8ff" stroke="#172a3a" stroke-width="1.8" stroke-linejoin="round"></path>' +
  '<path d="M9.6 7.6h4.8c-.6 1.2-1.5 1.9-2.4 2.6-.9-.7-1.8-1.4-2.4-2.6zM9.2 17.4c.7-1.5 1.8-2.2 2.8-2.9 1 .7 2.1 1.4 2.8 2.9z"' +
  ' fill="#ffc93c"></path>' +
  '<rect x="5.2" y="2.6" width="13.6" height="3.2" rx="1.6" fill="#ffc93c" stroke="#172a3a" stroke-width="1.8"></rect>' +
  '<rect x="5.2" y="18.2" width="13.6" height="3.2" rx="1.6" fill="#ffc93c" stroke="#172a3a" stroke-width="1.8"></rect>' +
  "</svg>";

/**
 * Ensure a cooldown badge contains the hourglass + label structure.
 * @param {HTMLElement | null | undefined} badgeEl
 */
export function ensureCooldownBadgeStructure(badgeEl) {
  if (!badgeEl || badgeEl.querySelector(".retention-cooldown-badge__label")) return;
  badgeEl.innerHTML =
    RETENTION_COOLDOWN_HOURGLASS_HTML +
    '<span class="retention-cooldown-badge__label"></span>';
}

/**
 * @param {HTMLElement | null | undefined} badgeEl
 * @param {number} ms
 */
export function setCooldownBadgeLabel(badgeEl, ms) {
  if (!badgeEl) return;
  ensureCooldownBadgeStructure(badgeEl);
  var label = badgeEl.querySelector(".retention-cooldown-badge__label");
  if (label) label.textContent = formatCooldownLabel(ms);
}

/**
 * @param {number} step 1–7
 * @returns {import("../config/retention-config.js").RetentionGrant}
 */
export function getDailyRewardForStep(step) {
  var idx = Math.max(1, Math.min(RETENTION_DAILY_TRACK_LENGTH, Math.floor(Number(step) || 1))) - 1;
  return Object.assign({}, RETENTION_DAILY_REWARDS[idx] || RETENTION_DAILY_REWARDS[0]);
}

/**
 * @param {ReturnType<typeof getDefaultRetentionState>} state
 * @param {number} [nowMs]
 * @returns {{ state: ReturnType<typeof getDefaultRetentionState>, grant: import("../config/retention-config.js").RetentionGrant }}
 */
export function claimDailyBonus(state, nowMs) {
  var next = normalizeRetentionState(state);
  var today = getLocalDateStringFromMs(nowMs);
  if (next.dailyBonusLastClaimDate === today) {
    return { state: next, grant: {} };
  }
  var step = next.dailyBonusStep;
  var grant = getDailyRewardForStep(step);
  next.dailyBonusLastClaimDate = today;
  next.dailyBonusStep = step >= RETENTION_DAILY_TRACK_LENGTH ? 1 : step + 1;
  return { state: next, grant: grant };
}

/**
 * @param {() => number} [rng]
 * @returns {number} segment index 0–7
 */
export function pickWheelSegmentIndex(rng) {
  var rollFn = typeof rng === "function" ? rng : Math.random;
  var roll = rollFn();
  var acc = 0;
  for (var i = 0; i < RETENTION_WHEEL_SEGMENTS.length; i++) {
    acc += RETENTION_WHEEL_SEGMENTS[i].weight;
    if (roll <= acc) return i;
  }
  return RETENTION_WHEEL_SEGMENTS.length - 1;
}

/**
 * @param {number} segmentIndex
 * @returns {import("../config/retention-config.js").RetentionGrant}
 */
export function getWheelGrantForSegment(segmentIndex) {
  var seg = RETENTION_WHEEL_SEGMENTS[segmentIndex];
  return seg ? Object.assign({}, seg.grant) : {};
}

/**
 * @param {ReturnType<typeof getDefaultRetentionState>} state
 * @param {"free"|"ad"} mode
 * @param {number} [nowMs]
 * @returns {{ ok: boolean, state: ReturnType<typeof getDefaultRetentionState> }}
 */
export function markWheelSpinUsed(state, mode, nowMs) {
  var next = normalizeRetentionState(state);
  var now = nowMs != null ? nowMs : Date.now();
  if (mode === "free") {
    next.wheelLastFreeSpinAt = now;
  } else if (mode === "ad") {
    next.wheelLastAdSpinAt = now;
  }
  return { ok: true, state: next };
}

/**
 * @param {ReturnType<typeof getDefaultRetentionState>} state
 * @param {number} [nowMs]
 * @returns {boolean}
 */
export function canUseWheelAdSpin(state, nowMs) {
  void state;
  void nowMs;
  return true;
}

/**
 * @param {ReturnType<typeof getDefaultRetentionState>} state
 * @param {number} [nowMs]
 * @returns {boolean}
 */
export function canUseGemsAd(state, nowMs) {
  return getCooldownRemainingMs(state.gemsAdLastAt, RETENTION_GEMS_AD_COOLDOWN_MS, nowMs) <= 0;
}

/**
 * @param {ReturnType<typeof getDefaultRetentionState>} state
 * @param {number} [nowMs]
 * @returns {ReturnType<typeof getDefaultRetentionState>}
 */
export function markGemsAdUsed(state, nowMs) {
  var next = normalizeRetentionState(state);
  next.gemsAdLastAt = nowMs != null ? nowMs : Date.now();
  return next;
}

/**
 * @param {ReturnType<typeof getDefaultRetentionState>} state
 * @param {number} [nowMs]
 * @returns {ReturnType<typeof getDefaultRetentionState>}
 */
export function markDailyAutoShown(state, nowMs) {
  var next = normalizeRetentionState(state);
  next.dailyBonusAutoShownDate = getLocalDateStringFromMs(nowMs);
  return next;
}

/**
 * @param {number} step
 * @returns {string}
 */
export function describeDailyStepReward(step) {
  var grant = getDailyRewardForStep(step);
  if (step === 7) return "FREE GIFT!";
  return formatGrantToast(grant);
}

/**
 * @param {import("../config/retention-config.js").RetentionGrant} grant
 * @returns {string}
 */
export function formatGrantToast(grant) {
  var parts = [];
  if (grant.gems) parts.push("+" + grant.gems + " gems");
  if (grant.shakes) parts.push("+" + grant.shakes + " shakes");
  if (grant.clears) parts.push("+" + grant.clears + " clears");
  if (grant.shrinks) parts.push("+" + grant.shrinks + " shrinks");
  return parts.join(", ") || "Reward claimed!";
}

/**
 * Clockwise segment start angle (deg) from the top pointer; matches labels + spin stop math.
 * @param {number} segmentIndex
 * @param {number} [segmentCount]
 * @returns {number}
 */
export function getWheelSegmentStartDeg(segmentIndex, segmentCount) {
  var count = segmentCount || RETENTION_WHEEL_SEGMENTS.length;
  var segmentAngle = 360 / count;
  var index = Math.floor(Number(segmentIndex) || 0);
  return ((index % count) + count) % count * segmentAngle;
}

/**
 * @param {number} segmentCount
 * @param {string[]} colors
 * @returns {string}
 */
export function buildWheelFaceConicGradient(segmentCount, colors) {
  var count = segmentCount || RETENTION_WHEEL_SEGMENTS.length;
  var segmentAngle = 360 / count;
  var stops = [];
  for (var i = 0; i < count; i++) {
    var start = i * segmentAngle;
    var end = (i + 1) * segmentAngle;
    var color = colors[i % colors.length];
    stops.push(color + " " + start + "deg " + end + "deg");
  }
  return "conic-gradient(" + stops.join(", ") + ")";
}

/**
 * Reward rows for UI: resource icon key + amount string (no "+" prefix).
 * @param {import("../config/retention-config.js").RetentionGrant} [grant]
 * @returns {Array<{ icon: string, amount: string }>}
 */
export function getGrantRewardDisplayItems(grant) {
  grant = grant || {};
  /** @type {Array<{ icon: string, amount: string }>} */
  var items = [];
  if (grant.gems) items.push({ icon: "gem", amount: String(grant.gems) });
  if (grant.shakes) items.push({ icon: "buttonShake", amount: String(grant.shakes) });
  if (grant.clears) items.push({ icon: "buttonClear", amount: String(grant.clears) });
  if (grant.shrinks) items.push({ icon: "buttonShrink", amount: String(grant.shrinks) });
  return items;
}

/**
 * Wheel wedge display: resource icon + amount label.
 * @param {{ grant: import("../config/retention-config.js").RetentionGrant }} segment
 * @returns {{ icon: string, amount: string }}
 */
export function getWheelSegmentDisplay(segment) {
  var grant = segment && segment.grant ? segment.grant : {};
  var items = getGrantRewardDisplayItems(grant);
  if (items.length) return items[0];
  return { icon: "gem", amount: "?" };
}

/**
 * Wheel reward claim button label (aria-label).
 * @param {import("../config/retention-config.js").RetentionGrant} grant
 * @returns {{ title: string, amount: string, icon: string }}
 */
export function getWheelRewardClaimCopy(grant) {
  if (!grant) return { title: "Reward", amount: "?", icon: "gem" };
  if (grant.gems) return { title: "Gem", amount: String(grant.gems), icon: "gem" };
  if (grant.shakes) return { title: "Shake", amount: String(grant.shakes), icon: "buttonShake" };
  if (grant.clears) return { title: "Clear", amount: String(grant.clears), icon: "buttonClear" };
  if (grant.shrinks) return { title: "Shrink", amount: String(grant.shrinks), icon: "buttonShrink" };
  return { title: "Reward", amount: "?", icon: "gem" };
}

/**
 * Wheel center claim button — always visible; enabled only after a spin stops.
 * @param {HTMLElement | null | undefined} btn
 * @param {{ enabled?: boolean, grant?: import("../config/retention-config.js").RetentionGrant }} opts
 */
export function applyWheelRewardClaimButtonState(btn, opts) {
  if (!btn) return;
  opts = opts || {};
  btn.classList.remove("hidden");
  if (opts.enabled) {
    var copy = getWheelRewardClaimCopy(opts.grant);
    btn.setAttribute("aria-label", "Claim " + copy.title + " " + copy.amount);
    btn.disabled = false;
    return;
  }
  btn.disabled = true;
  btn.setAttribute("aria-label", "Claim reward");
}
